import { debounce } from 'debounce';
import { parseInt } from 'lodash';
import * as customerApi from '../../api/customer';
import * as fileApi from '../../api/file';
import { getSocketId } from './realtime';
import { BIZ_TYPES } from './costants/biz-types';
import {
  numberToString,
  stringToNumber,
} from '../../handlebars-helpers/transform';

let isFirstCall = true;
let debouncedSave = null;

export function customerPage(customer: any, isAdmin: string) {
  return {
    isAdmin: isAdmin === 'Y',
    isFinished: false,
    isLoading: false,
    customer: JSON.parse(decodeURIComponent(customer)),
    executive: {
      position: '',
      name: '',
      socialNumber1: '',
      socialNumber2: '',
      isStockholder: '주식보유',
      address: {
        addr1: '',
        addr2: '',
        building: '',
      },
    },
    stockholder: {
      name: '',
      socialNumber1: '',
      socialNumber2: '',
      address: {
        addr1: '',
        addr2: '',
        building: '',
      },
      isMain: false,
    },
    objective: '',
    objective2: '',

    init() {
      debouncedSave = debounce(this.saveCustomer.bind(this), 500);

      this.$watch('customer.capital', () => {
        for (let i = 0; i < this.customer.stockholders.length; i++) {
          this.customer.stockholders[i].rate = (
            (stringToNumber(this.customer.stockholders[i].stockCount) /
              stringToNumber(this.totalStockQty)) *
            100
          ).toFixed(1);

          this.customer.stockholders[i].money = numberToString(
            stringToNumber(this.customer.stockholders[i].stockCount) *
              stringToNumber(this.customer.capital.moneyPerStock),
          );
        }
      });

      this.$watch('customer.capital.capital', (value, oldValue) => {
        const v = parseInt(value.replace(/,/g, ''));
        if (isNaN(v)) {
          this.customer.capital.capital = '0';
        } else {
          this.customer.capital.capital = new Intl.NumberFormat().format(v);
        }
      });

      this.$watch('customer.capital.moneyPerStock', (value, oldValue) => {
        const v = parseInt(value.replace(/,/g, ''));
        if (isNaN(v)) {
          this.customer.capital.moneyPerStock = '0';
        } else {
          this.customer.capital.moneyPerStock = new Intl.NumberFormat().format(
            v,
          );
        }
      });
    },

    async saveCustomer() {
      for (const stockholder of this.customer.stockholders) {
        let position = '';
        for (const executive of this.customer.executives) {
          if (executive.name === stockholder.name) {
            position = executive.position;
          }
        }

        stockholder.position = position;
      }

      return customerApi.update(this.customer.recordId, {
        companyNameKr: this.customer.companyNameKr,
        companyNameEng: this.customer.companyNameEng,
        address: this.customer.address,
        capital: {
          ...this.customer.capital,
          stockCount: this.totalStockQty,
        },
        executives: this.customer.executives,
        stockholders: this.customer.stockholders,
        objectives: this.customer.objectives,
        saveStates: this.customer.saveStates,
        executiveType: this.customer.executiveType,
        balanceCertificate: this.customer.balanceCertificate,
        residentCertificates: this.customer.residentCertificates,
        etcFiles: this.customer.etcFiles,
        companyArea: this.companyArea,
        stampFiles: this.customer.stampFiles,
        executivesWithoutStock: this.executivesWithoutStock,
        registrationTax: this.registrationTax,
        isFinished: this.isFinished,
      });
    },

    async save(movePage = true) {
      this.isLoading = true;
      this.isFinished = this.isAdmin;

      await this.saveCustomer()
        .then(() => {
          if (movePage) {
            document.location.href = `/customer/${this.customer.recordId}/summary`;
          }
        })
        .catch((error) => {
          if (error.response.status === 400) {
            const data = error.response.data.data;

            alert(data[Object.keys(data)[0]][0]);
          }
        });
      this.isLoading = false;
    },

    async changeFile(event: any, type: string) {
      if (event.target.files.length === 0) {
        return;
      }

      for (const file of event.target.files) {
        const response = await fileApi.uploadImage(
          this.customer.recordId,
          file,
        );

        if (type === 'balance') {
          this.customer.balanceCertificate = {
            fileName: response.data.fileName,
            originalFileName: response.data.originalFileName,
          };
        } else if (type === 'resident') {
          this.customer.residentCertificates.push({
            fileName: response.data.fileName,
            originalFileName: response.data.originalFileName,
          });
        } else if (type === 'etc') {
          this.customer.etcFiles.push({
            fileName: response.data.fileName,
            originalFileName: response.data.originalFileName,
          });
        } else if (type === 'stamp') {
          this.customer.stampFiles.push({
            fileName: response.data.fileName,
            originalFileName: response.data.originalFileName,
          });
        }
      }

      event.target.value = '';
      await this.updateSection(null);
    },

    get companyArea() {
      if (
        this.customer.address.addr1 === '' &&
        this.customer.address.addr2 === '' &&
        this.customer.address.building === ''
      ) {
        return this.customer.companyArea;
      }

      if (
        /(서울특별시)/g.exec(this.customer.address.addr1) !== null ||
        /경기도 (의정부시|구리시|하남시|고양시|수원시|성남시|안양시|부천시|광명시|과천시|의왕시|군포시|시흥시)/g.exec(
          this.customer.address.addr1,
        ) !== null ||
        ((this.customer.address.sido === '경기도' ||
          /(경기도)/g.exec(this.customer.address.addr1) !== null) &&
          /(호평동|평내동|금곡동|일패동|이패동|삼패동|다산동|수석동|지금동|도농동)/g.exec(
            this.customer.address.bname,
          )) ||
        ((this.customer.address.sido === '인천광역시' ||
          /(인천광역시)/g.exec(this.customer.address.addr1) !== null) &&
          /(강화군|옹진군)/g.exec(this.customer.address.addr1) === null &&
          /(대곡동|불로동|마전동|왕길동|당하동|원당동|운남동|운복동|운서동|중산동|남북동|덕교동|을왕동|무의동|송도동)/g.exec(
            this.customer.address.bname,
          ) === null &&
          /(청라동|송도동)/g.exec(this.customer.address.building) === null)
      ) {
        return '서울/경기/인천';
      }

      return '비과밀억제권역';
    },

    get capitalAsNumber() {
      const n = parseInt(this.customer.capital.capital.replace(/,/g, ''));

      if (isNaN(n)) return 0;
      return n;
    },

    get registrationTax() {
      const tax =
        this.capitalAsNumber * 0.004 < 112500
          ? 112500
          : this.capitalAsNumber * 0.004;

      if (this.companyArea === '서울/경기/인천') {
        return Math.ceil(tax * 3);
      }

      return Math.ceil(tax);
    },

    isShowEditButton(index: number) {
      if (this.isAdmin) {
        return true;
      } else {
        return this.customer.saveStates[index] !== '완료';
      }
    },

    isEditable(index: number) {
      if (this.isAdmin) {
        return this.customer.saveStates[index] !== '완료';
      } else {
        return this.customer.saveStates[index] === '작성중';
      }
    },

    deleteExecutive(index: number) {
      const socialNumber = this.customer.executives[index].socialNumber;
      const name = this.customer.executives[index].name;

      if (this.isStockholder(name, socialNumber) === '주식보유') {
        this.customer.stockholders.splice(
          this.customer.stockholders.findIndex(
            (holder: any) =>
              holder.name === name && holder.socialNumber === socialNumber,
          ),
          1,
        );
      }

      this.customer.executives.splice(index, 1);
    },

    stockHolderChanged(index: number, event: Event) {
      const v = (event.target as HTMLInputElement).value;
      const executive = this.customer.executives[index];
      const name = executive.name;
      const socialNumber = executive.socialNumber;

      if (v === '주식보유') {
        if (this.isStockholder(name) === '주식미보유') {
          this.customer.stockholders.push({
            name: executive.name,
            position: executive.position,
            socialNumber: executive.socialNumber,
            address: {
              addr1: executive.address.addr1,
              addr2: executive.address.addr2,
              building: executive.address.building,
            },
            stockCount: '',
            money: '',
            rate: '',
            isMain: false,
          });
        } else {
          this.customer.stockholders[
            this.customer.stockholders.findIndex(
              (holder: any) => holder.socialNumber === socialNumber,
            )
          ] = {
            name: executive.name,
            position: executive.position,
            socialNumber: executive.socialNumber,
            address: {
              addr1: executive.address.addr1,
              addr2: executive.address.addr2,
              building: executive.address.building,
            },
            stockCount: '',
            money: '',
            rate: '',
            isMain: false,
          };
        }
      } else if (
        v === '주식미보유' &&
        this.isStockholder(name) === '주식보유'
      ) {
        this.customer.stockholders.splice(
          this.customer.stockholders.findIndex(
            (holder: any) => holder.name === name,
          ),
          1,
        );
      }
    },

    deleteFile(type: string, index: number = null) {
      if (type === 'resident') {
        this.customer.residentCertificates.splice(index, 1);
      } else if (type === 'etc') {
        this.customer.etcFiles.splice(index, 1);
      } else if (type === 'balance') {
        this.customer.balanceCertificate = {
          fileName: '',
          originalFileName: '',
        };
      } else if (type === 'stamp') {
        this.customer.stampFiles.splice(index, 1);
      }

      this.updateSection(null);
    },

    editSection(event: any) {
      const wrap = event.target.closest('.cnt_wrap');
      const cnt = wrap.dataset.section;

      this.customer.saveStates[cnt - 1] = '작성중';

      if (!wrap.classList.contains('on')) {
        wrap.classList.add('on');
      }
    },

    updateSection(event: any) {
      let wrap, cnt, oldState;
      if (event) {
        const wrap = event.target.closest('.cnt_wrap');
        const cnt = wrap.dataset.section;
        oldState = this.customer.saveStates[cnt - 1];
        if (this.isAdmin) {
          this.customer.saveStates[cnt - 1] = '완료';
        } else {
          this.customer.saveStates[cnt - 1] = '검토대기중';
        }
      }

      this.isLoading = true;

      this.saveCustomer()
        .catch((error) => {
          if (error.response.status === 400) {
            if (event) {
              this.customer.saveStates[cnt - 1] = oldState;
            }

            const data = error.response.data.data;
            alert(data[Object.keys(data)[0]][0]);
          }
        })
        .then(() => {
          if (wrap && event) {
            wrap.classList.remove('on');
          }
          this.isLoading = false;
        });
    },

    isSectionDisabled(sectionIndex: number) {
      return !this.isEditable(sectionIndex);
    },

    get objectAutoCompleteTexts() {
      return BIZ_TYPES.filter(
        (type) => type.indexOf(this.objective) >= 0,
      ).filter((type) => {
        return this.customer.objectives.indexOf(type) < 0;
      });
    },

    get remainedStockholderRate() {
      const totalRate = this.customer.stockholders
        .map((holder: any) => parseFloat(holder.rate) || 0)
        .reduce((a: number, b: number) => a + b, 0);

      return 100 - totalRate;
    },

    get remainedStockholderStockCount() {
      const totalStockCount = this.customer.stockholders
        .map((holder: any) => stringToNumber(holder.stockCount) || 0)
        .reduce((a: number, b: number) => a + b, 0);

      return numberToString(
        stringToNumber(this.totalStockQty) - totalStockCount,
      );
    },

    get remainedStockholderMoney() {
      const totalMoney = this.customer.stockholders
        .map((holder: any) => stringToNumber(holder.money) || 0)
        .reduce((a: number, b: number) => a + b, 0);

      return numberToString(
        stringToNumber(this.customer.capital.capital) - totalMoney,
      );
    },

    get totalStockQty() {
      const number = Math.floor(
        parseInt(String(this.customer.capital.capital).replace(/,/g, '')) /
          parseInt(
            String(this.customer.capital.moneyPerStock).replace(/,/g, ''),
          ),
      );

      if (isNaN(number) || number === Number.POSITIVE_INFINITY) return '0';

      return new Intl.NumberFormat().format(number);
    },

    get executivesWithoutStock() {
      return this.customer.executives.filter(
        (executive: any) =>
          this.isStockholder(executive.name, executive.socialNumber) ===
          '주식미보유',
      );
    },

    updateObjective(event: any) {
      if ([37, 38, 39, 40].indexOf(event.keyCode) >= 0) {
        return;
      }

      this.objective = event.target.value;
    },

    selectObjective(direction = 'down') {
      const dom = document.getElementById('objective-list');
      const popupLayer = dom.closest('.objective-popup');
      const selectedLi = dom.querySelector('li.selected');
      let activeDom = null;

      if (selectedLi) {
        selectedLi.classList.remove('selected');

        if (direction === 'down') {
          activeDom = selectedLi.nextElementSibling;
        } else {
          activeDom = selectedLi.previousElementSibling;
        }
      } else {
        if (direction === 'up') return;
        activeDom = dom.querySelector('li');
      }

      if (!activeDom) {
        return;
      }

      activeDom.classList.add('selected');
      popupLayer.scrollTo(0, activeDom.offsetTop);
      this.objective2 = activeDom.innerText.trim();
    },

    addObjective(txt = null) {
      let value = '';

      if (txt === null) {
        if (this.objective2 !== '') {
          value = this.objective2.trim();
        } else if (this.objective.trim() !== '') {
          value = this.objective.trim();
        }
      } else {
        value = txt;
      }

      if (value === '') return;

      if (
        this.customer.objectives.filter((obj) => obj === value).length === 0
      ) {
        this.customer.objectives.push(value);
      }

      this.objective = '';
      this.objective2 = '';
    },

    isStockholder(name: string, socialNumber?: string) {
      return this.customer.stockholders.findIndex((holder: any) =>
        holder.name === name && !socialNumber
          ? true
          : holder.socialNumber === socialNumber,
      ) >= 0
        ? '주식보유'
        : '주식미보유';
    },

    calculateHolder(holder: any, base: string) {
      if (base === 'rate') {
        if (holder.rate === '') return;
        holder.stockCount = numberToString(
          (stringToNumber(this.totalStockQty) * parseFloat(holder.rate)) / 100,
        );
        holder.money = numberToString(
          stringToNumber(holder.stockCount) *
            stringToNumber(this.customer.capital.moneyPerStock),
        );
      } else {
        if (holder.stockCount === '' || holder.stockCount === '0') return;
        holder.rate = (
          (stringToNumber(holder.stockCount) /
            stringToNumber(this.totalStockQty)) *
          100
        ).toFixed(1);
        holder.money = numberToString(
          stringToNumber(holder.stockCount) *
            stringToNumber(this.customer.capital.moneyPerStock),
        );
      }
    },

    editStockholder(index: number) {
      const socialNumbers =
        this.customer.stockholders[index].socialNumber.split('-');

      this.stockholder = {
        index,
        name: this.customer.stockholders[index].name,
        socialNumber1: socialNumbers[0],
        socialNumber2: socialNumbers[1],
        address: {
          addr1: this.customer.stockholders[index].address.addr1,
          addr2: this.customer.stockholders[index].address.addr2,
          building: this.customer.stockholders[index].address.building,
        },
        isMain: this.customer.stockholders[index].isMain === true,
      };

      openAddStockholderModal();
    },

    mainStockHolderChanged(index: number) {
      this.customer.stockholders.forEach((holder: any, idx: number) => {
        if (idx !== index) {
          holder.isMain = false;
        }
      });
    },

    addStockholder() {
      const socialNumber = `${this.stockholder.socialNumber1}-${this.stockholder.socialNumber2}`;
      let index;

      if (this.stockholder.index !== undefined) {
        index = this.stockholder.index;
      } else {
        index = this.customer.stockholders.findIndex(
          (holder: any) =>
            holder.name === this.stockholder.name &&
            holder.socialNumber === socialNumber,
        );
      }

      if (index >= 0) {
        this.customer.stockholders[index].name = this.stockholder.name;
        this.customer.stockholders[index].socialNumber = socialNumber;
        this.customer.stockholders[index].address = {
          addr1: this.stockholder.address.addr1,
          addr2: this.stockholder.address.addr2,
          building: this.stockholder.address.building,
        };
      } else {
        this.customer.stockholders.push({
          position: '',
          name: this.stockholder.name,
          socialNumber: socialNumber,
          address: {
            addr1: this.stockholder.address.addr1,
            addr2: this.stockholder.address.addr2,
            building: this.stockholder.address.building,
          },
          stockCount: '',
          money: '',
          rate: '',
          isMain: false,
        });
      }

      this.clearStockholder();
    },

    clearStockholder() {
      this.stockholder = {
        name: '',
        socialNumber1: '',
        socialNumber2: '',
        address: {
          addr1: '',
          addr2: '',
          building: '',
        },
        isMain: false,
      };
    },

    editExecutive(index: number) {
      const socialNumbers =
        this.customer.executives[index].socialNumber.split('-');

      this.executive = {
        index,
        position: this.customer.executives[index].position,
        name: this.customer.executives[index].name,
        socialNumber1: socialNumbers[0],
        socialNumber2: socialNumbers[1],
        isStockholder: this.isStockholder(this.customer.executives[index].name),
        address: {
          addr1: this.customer.executives[index].address.addr1,
          addr2: this.customer.executives[index].address.addr2,
          building: this.customer.executives[index].address.building,
        },
      };

      openAddExecutiveModal();
    },

    clearExecutive() {
      this.executive = {
        position: '',
        name: '',
        socialNumber1: '',
        socialNumber2: '',
        isStockholder: '주식보유',
        address: {
          addr1: '',
          addr2: '',
          building: '',
        },
      };
    },

    addExecutive() {
      const socialNumber = `${this.executive.socialNumber1}-${this.executive.socialNumber2}`;
      let index;

      if (this.executive.index !== undefined) {
        index = this.executive.index;
      } else {
        index = this.customer.executives.findIndex(
          (executive: any) =>
            executive.name === this.executive.name &&
            executive.socialNumber === socialNumber,
        );
      }

      if (index >= 0) {
        this.customer.executives[index] = {
          position: this.executive.position,
          name: this.executive.name,
          socialNumber,
          address: {
            addr1: this.executive.address.addr1,
            addr2: this.executive.address.addr2,
            building: this.executive.address.building,
          },
        };
      } else {
        this.customer.executives.push({
          position: this.executive.position,
          name: this.executive.name,
          socialNumber,
          address: {
            addr1: this.executive.address.addr1,
            addr2: this.executive.address.addr2,
            building: this.executive.address.building,
          },
        });
      }

      if (this.executive.isStockholder === '주식보유') {
        if (this.isStockholder(this.executive.name) === '주식미보유') {
          this.customer.stockholders.push({
            position: this.executive.position,
            name: this.executive.name,
            socialNumber: socialNumber,
            address: {
              addr1: this.executive.address.addr1,
              addr2: this.executive.address.addr2,
              building: this.executive.address.building,
            },
            stockCount: '',
            money: '',
            rate: '',
          });
        } else {
          const stockholder =
            this.customer.stockholders[
              this.customer.stockholders.findIndex(
                (holder: any) => holder.name === this.executive.name,
              )
            ];

          stockholder.position = this.executive.position;
          stockholder.name = this.executive.name;
          stockholder.socialNumber = socialNumber;
          stockholder.address = {
            addr1: this.executive.address.addr1,
            addr2: this.executive.address.addr2,
            building: this.executive.address.building,
          };
        }
      } else {
        if (this.isStockholder(this.executive.name) === '주식보유') {
          this.customer.stockholders.splice(
            this.customer.stockholders.findIndex(
              (holder: any) => holder.name === this.executive.name,
            ),
            1,
          );
        }
      }

      this.clearExecutive();
    },

    openImage(path: string) {
      if (path === '') return;
      window.open(`/file/${path}`);
    },

    checkDuplicatedObjective(index: number) {
      if (
        this.customer.objectives.filter(
          (o: any) => o === this.customer.objectives[index],
        ).length > 1
      ) {
        this.customer.objectives.splice(index, 1);
      }
    },
  };
}

let syncTime = null;
const updateFn = debounce((recordId, value) => {
  window.js.realtime.sync(recordId, value);
}, 500);

export function init() {
  window.js.realtime.connect(this.customer.recordId, (data: any) => {
    if (getSocketId() !== data.senderId) {
      this.customer = JSON.parse(JSON.stringify(data.customer));
      syncTime = new Date().getTime();
      this.customer.syncTime = syncTime;
    }
  });

  this.$watch('customer', (value: any) => {
    if (syncTime && value.syncTime === syncTime) {
      syncTime = null;
      return;
    }

    if (isFirstCall) {
      isFirstCall = false;
    } else {
      debouncedSave(false);
    }

    updateFn(this.customer.recordId, value);
  });
}

export function openAddExecutiveModal() {
  document.getElementById('modal_bg').classList.add('on');
  document.getElementById('exc_wrap').classList.add('on');
}

export function openAddStockholderModal() {
  document.getElementById('modal_bg').classList.add('on');
  document.getElementById('sha_wrap').classList.add('on');
}
